import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Input,
  ViewChild,
} from '@angular/core';
import { get, isNil } from 'lodash';
import { calculateTextWidth } from '@shared/utils';
import { ICellRendererParams, IHeaderParams } from 'ag-grid-community';

@Component({
  selector: 'xea-popover-base',
  templateUrl: 'popover-base.component.html',
  styleUrls: ['./popover-base.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class XEAPopoverComponent implements AfterViewInit {
  /**
   * text for popover
   */
  @Input() public popoverText: string;
  /**
   * class for cell content
   */
  @Input() public cellClass: string;
  /**
   * position for popover, if actualPosition exit, use actualPosition
   */
  @Input() public position: string;
  /**
   * we use popoverParentNode for popover width calculation,
   * if not provided, the default parent node (defaultParentNode) will be used.
   */
  @Input() public popoverParentNode: string;
  /**
   * If the popover needs to be displayed permanently, pass alwaysShowPopover as true.
   */
  @Input() public alwaysShowPopover: boolean = false;
  /**
   * params for cell renderer action
   */
  @Input() public params: ICellRendererParams | IHeaderParams | null = null;

  @ViewChild('divElement', { static: false }) public divElement: ElementRef;
  @ViewChild('popoverElement', { static: false })
  public popoverElement: ElementRef;
  public popoverVisible = false;
  public actualPosition: string = '';
  public maxWidth: number = 0;
  public defaultParentNode: string = '.modal-content';
  /* padding for popover container */
  public popoverPadding: number = 20;
  public popoverStyle: {
    width: string;
    left: string;
  } = {
    width: '0px',
    left: '0px',
  };
  public arrowStyle: {
    left: string;
  } = {
    left: '0px',
  };
  private originalZIndex: string | null = null;
  private rowElement: HTMLElement | null = null;
  private popoverParentNodeElement: HTMLElement | null = null;
  private btnGroupVerticalWidth: number | null = null;
  /** Border + visual breathing space offset */
  private readonly BORDER_OFFSET = 1;
  /** Half of .popover-arrow's border box, i.e. the offset of its tip. */
  private readonly ARROW_HALF_WIDTH = 10;
  /** How far into its own cell the arrow leans, as a fraction of cell width. */
  private readonly ARROW_CELL_RATIO = 0.1;
  /** Extra nudge to the right on top of the proportional lean. */
  private readonly ARROW_CELL_OFFSET = 10;
  /**
   * Since the calculation of the text width is not very precise,
   * please add an error tolerance to prevent the popover from not displaying.
   */
  public tolerance = 10;

  @HostListener('document:click', ['$event'])
  public documentOnClick(event: MouseEvent): void {
    if (!this.divElement?.nativeElement.contains(event.target)) {
      this.popoverVisible = false;
      if (this.rowElement && !isNil(this.originalZIndex)) {
        this.originalZIndex = null;
        setTimeout(() => {
          this.rowElement.style.zIndex = this.originalZIndex;
        }, 0);
      }
    }
  }

  public ngAfterViewInit(): void {
    const targetElement = this.divElement?.nativeElement?.closest(
      this.popoverParentNode || this.defaultParentNode,
    );
    if (!targetElement) {
      // If targetElement is missing, popover width calculation fails and triggers a console warning for debugging.
      // eslint-disable-next-line no-console
      console.warn('Init popover width failed');
      return;
    }
    this.popoverParentNodeElement = targetElement;
    const width = get(targetElement, 'offsetWidth', 0);
    const computedStyle = window.getComputedStyle(targetElement);
    const paddingLeft = parseFloat(computedStyle.paddingLeft);
    const paddingRight = parseFloat(computedStyle.paddingRight);
    // Max pop-up width = target node width(not include target node padding) - pop-up padding.
    this.maxWidth = width - paddingLeft - paddingRight - this.popoverPadding;
  }

  public showPopover(): void {
    if (
      !this.divElement?.nativeElement ||
      !this.maxWidth ||
      !this.popoverText
    ) {
      this.popoverVisible = false;
      return;
    }

    const { fontFamily, fontSize, fontWeight } = window.getComputedStyle(
      this.divElement.nativeElement,
    );
    const popoverTextWidth =
      calculateTextWidth(this.popoverText, fontSize, {
        fontFamily,
        fontWeight,
      }) + this.tolerance;

    // show popover only when necessary
    if (!this.alwaysShowPopover) {
      const actualWidth = get(this.divElement, 'nativeElement.clientWidth', 0);
      const cellRect = this.divElement.nativeElement.getBoundingClientRect();
      const documentHeight = document.documentElement.scrollHeight;
      const cellBottomAbsolute = cellRect.bottom + window.scrollY;
      const distanceToPageBottom = documentHeight - cellBottomAbsolute;

      if (popoverTextWidth > actualWidth) {
        this.actualPosition = distanceToPageBottom < 180 ? 'top' : 'bottom';
      } else {
        this.popoverVisible = false;
        return;
      }
    }
    // If the parent node is not the default parent node, check if there is a btn-group-vertical node inside
    if (
      this.popoverParentNode !== this.defaultParentNode &&
      this.popoverParentNodeElement.querySelector('.btn-group-vertical') &&
      isNil(this.btnGroupVerticalWidth)
    ) {
      this.btnGroupVerticalWidth = get(
        this.popoverParentNodeElement.querySelector('.btn-group-vertical'),
        'offsetWidth',
        0,
      );
    }

    // Calculate the horizontal (left) offset and the width for the popover
    const cellElement = this.getCellElement();
    this.rowElement = cellElement ? cellElement.closest('.ag-row') : null;

    this.updatePopoverGeometry(cellElement, popoverTextWidth);

    if (this.rowElement) {
      if (!this.popoverVisible) {
        this.originalZIndex = this.rowElement.style.zIndex;
        setTimeout(() => {
          this.rowElement.style.zIndex = '1';
        }, 0);
      } else {
        this.originalZIndex = null;
        setTimeout(() => {
          this.rowElement.style.zIndex = this.originalZIndex;
        }, 0);
      }
    }

    this.popoverVisible = !this.popoverVisible;
  }

  private getCellElement(): HTMLElement | null {
    if (this.params && 'eGridCell' in this.params && this.params.eGridCell) {
      return <HTMLElement>this.params.eGridCell;
    }

    if (this.params && 'eGridHeader' in this.params && this.params.eGridHeader) {
      return <HTMLElement>this.params.eGridHeader;
    }

    return null;
  }

  /**
   * Body cells are forced to `position: static` so the popover can escape their
   * `overflow: hidden`, which leaves the surrounding `.ag-row` as its containing
   * block. Header cells stay positioned, so each one is its own containing
   * block. `offsetParent` resolves whichever applies, and its offset converts
   * the shared column coordinates into that local space. Those coordinates
   * scroll with the columns, so the visible window comes from the viewport's
   * `scrollLeft` rather than from the grid's own width.
   */
  private updatePopoverGeometry(
    cellElement: HTMLElement | null,
    popoverTextWidth: number,
  ): void {
    const cellLeft = cellElement ? cellElement.offsetLeft : 0;
    const cellWidth = cellElement
      ? cellElement.offsetWidth
      : get(this.divElement, 'nativeElement.offsetWidth', 0);
    const originLeft = get(
      this.divElement,
      'nativeElement.offsetParent.offsetLeft',
      0,
    );

    const viewport = this.getScrollViewport(cellElement);
    const visibleLeft = (viewport ? viewport.scrollLeft : 0) - originLeft;
    const visibleWidth = viewport ? viewport.clientWidth : this.maxWidth;

    // Never grow past the parent node, the visible window, or the space left
    // over by a vertical button group docked inside the parent.
    const widthBudget = Math.min(
      this.maxWidth - (this.btnGroupVerticalWidth ?? 0),
      visibleWidth,
    );
    const maxBoxWidth = Math.max(0, widthBudget - this.BORDER_OFFSET);
    const boxWidth = Math.min(
      popoverTextWidth + this.popoverPadding,
      maxBoxWidth,
    );

    // Align the popover with the left edge of its own cell, matching the
    // long-standing look, then pull it back inside the visible window.
    const cellLeftLocal = cellLeft - originLeft;
    const minLeft = visibleLeft + this.BORDER_OFFSET;
    const maxLeft = visibleLeft + visibleWidth - boxWidth - this.BORDER_OFFSET;
    const left =
      maxLeft <= minLeft
        ? minLeft
        : this.clamp(cellLeftLocal, minLeft, maxLeft);

    this.popoverStyle.width = `${boxWidth}px`;
    this.popoverStyle.left = `${left}px`;

    // The arrow leans towards the left of the cell, as it always has, but it is
    // pinned inside that cell so it can never point at a neighbouring column,
    // and kept off the tooltip's rounded corners.
    const arrowInset = this.ARROW_HALF_WIDTH + this.popoverPadding / 2;
    const cellInset = Math.min(arrowInset, cellWidth / 2);
    const anchorX = this.clamp(
      cellLeftLocal + cellWidth * this.ARROW_CELL_RATIO + this.ARROW_CELL_OFFSET,
      cellLeftLocal + cellInset,
      cellLeftLocal + cellWidth - cellInset,
    );
    const arrowTip = this.clamp(
      anchorX - left,
      arrowInset,
      Math.max(arrowInset, boxWidth - arrowInset),
    );
    this.arrowStyle.left = `${Math.max(0, arrowTip - this.ARROW_HALF_WIDTH)}px`;
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }

  /**
   * Header and body scroll in lockstep, so the body viewport is used as the
   * single source of truth for the horizontal offset and the visible width.
   */
  private getScrollViewport(
    cellElement: HTMLElement | null,
  ): HTMLElement | null {
    const root = cellElement?.closest('.ag-root');

    return <HTMLElement | null>(
      (root?.querySelector('.ag-center-cols-viewport') ?? null)
    );
  }
}
